import { useState, useEffect } from "react";
import {
  Box,
  Button,
  getColor,
  Icons,
  InputField,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "../index.scss";
import useLoginStore from "../../../redux/dispatcher/useLoginStore";
import {
  getUserInfoFromLocalStorage,
  setRedirectUrl,
} from "../../../shared/utils/auth";
import { useNavigate } from "react-router-dom";
import docApi from "../../../redux/api/DocApi";
import { nanoid } from "@reduxjs/toolkit";
import SimpleLinkPage from "../../../components/SimpleLinkPage";
import authApi from "../../../redux/api/LoginApi";
import dayjs from "dayjs";
import NoAuthPreview from "./NoAuthPreview";
import utilsCommon from "../../../shared/utils/common";
import { t } from "i18next";
import DownloadIng from "./DownloadIng";
import { LoginDto } from "../../../types/auth";

const WebusingLink = () => {
  const { loginState, getFileUrlFromUid } = useLoginStore();
  const toasts = useToast();
  const navigate = useNavigate();

  const [currStep, changeCurrStep] = useState("ING");
  const [isPwVisible, togglePWVisible] = useState(false); // 비밀번호 보임, 안보임 처리용
  const [simpleInfo, changeSimpleInfo] = useState({
    contentName: "",
    fileSize: 0,
  });
  const [checkIsLogin, changeCheckIsLogin] = useState(false);
  const [fileUrl, changeFileUrl] = useState("");
  const [password, changePassword] = useState("");
  const [err, changeErr] = useState("");
  const [apiTokenAfterPw, changeApiTokenAfterPW] = useState<string>("");
  useEffect(() => {
    changeCurrStep("ING");
    const authType = loginState.actionInfo.authType;
    if (loginState.actionInfo.urlAction === "HTMLDOWNLOAD") {
      // 로그인 여부 체크 필요
      if (!!loginState.loginInfo || checkIsLogin) {
        // handleDoActionByUid();
        changeCurrStep("SUCCESS");
      } else {
        setRedirectUrl(window.location.href);
        navigate(`${process.env.PUBLIC_URL}/login`);
      }
    } else if (authType === "LOGIN") {
      // 로그인 여부 체크 필요
      const user = getUserInfoFromLocalStorage() as string | "";
      const loginDto: LoginDto = JSON.parse(user);

      if (!!loginState.loginInfo || checkIsLogin) {
        if (loginDto?.ecoSystemLicenseStats?.allowWebView === true)
          navigate(
            `${process.env.PUBLIC_URL}/docs/preview?permission=view&syncId=${loginState.actionInfo.syncId}`
          );

        changeCurrStep("SUCCESS");
      } else {
        setRedirectUrl(window.location.href);
        navigate(`${process.env.PUBLIC_URL}/login`);
      }
    } else if (
      authType === "NONE" &&
      loginState.actionInfo.urlAction.includes("WEBUSING")
    ) {
      handleDoActionByUid();
    } else if (authType === "PASSWORD") {
      handleGetContentSimpleInfo();
    }
  }, []);
  useEffect(() => {
    changeCheckIsLogin(!!loginState.loginInfo);
  }, [loginState.loginInfo]);
  /** 비밀번호 링크일 경우, 문서명 표시를 위해 해당 function 호출 */
  const handleGetContentSimpleInfo = async () => {
    try {
      let resp: any = await docApi.getContentSimpleDetail(
        loginState.actionInfo.syncId
      );
      if (!!resp?.contentName) {
        changeSimpleInfo(resp);
      } else {
        toasts.toastMsg(nanoid(), t("file.alert.previewFail"), "error");
        changeCurrStep("FAIL");
      }
    } catch (err: any) {
      toasts.toastMsg(
        nanoid(),
        err?.errorMessage ?? t("file.alert.contentDelete"),
        "error"
      );
      changeCurrStep("FAIL");
    }
  };

  /** 비로그인 미리보기 상태 */
  const handleDoActionByUid = async (token?: string): Promise<void> => {
    if (!loginState.actionInfo) return;
    if (loginState.actionInfo.expiredTime < dayjs().valueOf()) {
      changeCurrStep("EXPIRED");
    }
    let resp: any = await getFileUrlFromUid({
      uid: loginState.actionInfo.uniqueId,
      token,
    });
    if (resp.error) {
      switch (resp.error.name) {
        case "DELETED":
          changeCurrStep("DELETED");
          toasts.toastMsg(nanoid(), t("file.alert.contentDelete"), "error");
          break;
        case "NO_SELECTED_WEB_OFFICE":
          changeCurrStep("FAIL");
          toasts.toastMsg(
            nanoid(),
            t("login.link.alert.noSelectedWebOffice"),
            "error"
          );
          break;
        default:
          changeCurrStep("FAIL");
          toasts.toastMsg(
            nanoid(),
            resp.error?.message ?? t("file.alert.previewFail"),
            "error"
          );
          break;
      }
    } else {
      changeFileUrl(resp.payload.accessUrl);
      changeCurrStep("SUCCESS");
    }
  };
  const handleCheckPassword = async () => {
    authApi
      .checkFilePWValid({
        uuid: loginState.actionInfo.uniqueId,
        password,
      })
      .then((resp) => {
        if (loginState.actionInfo.expiredTime < dayjs().valueOf()) {
          changeCurrStep("EXPIRED");
        } else if (loginState.actionInfo.urlAction.includes("WEBUSING")) {
          changeApiTokenAfterPW(resp.apiToken);
          handleDoActionByUid(resp.apiToken);
        } else {
          changeApiTokenAfterPW(resp.apiToken);
          changeCurrStep("SUCCESS");
        }
        changeErr("");
      })
      .catch((err) => {
        if (err.name === "INVALIED_PASSWORD") {
          changeErr("INVALIED_PASSWORD");
        }
      });
  };
  const renderCheckPassword = () => {
    return (
      <Box height={"100%"} width={"100%"} direction="column">
        <Box classes="error-header">
          <img
            src={utilsCommon.renderWrapsodyECOLogo()}
            alt={"logo"}
            height={46}
            className="cursor pointer"
            onClick={() => navigate(`${process.env.PUBLIC_URL}/home`)}
          />
        </Box>
        <Box alignItems="center" style={{ paddingTop: 188 }} direction="column">
          <img
            src={require("../../../shared/images/common/restrictClient.png")}
            alt={"error"}
          />
          <Typography
            type="h2"
            classes="mg mt-10"
            whiteSpace="break-spaces"
            textAlign="center"
          >
            {t("login.login.passwordPlaceholder")}
          </Typography>
          <Typography
            type="body1_md"
            classes="mg mt-26"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            style={{ marginTop: 26 }}
            whiteSpace="break-spaces"
            textAlign="center"
          >
            {t("login.link.enterPW", { 0: simpleInfo.contentName })}
          </Typography>
          <InputField
            placeholder={t("signup.passwordPlaceholder")}
            size="md"
            width={360}
            style={{ marginTop: 45 }}
            onChange={(e) => changePassword(e.target.value)}
            maxLength={4}
            type={isPwVisible ? "text" : "password"}
            helpMessage={
              err ? t("login.changePW.alert.notMatchPwMsg") : undefined
            }
            isError={err !== ""}
            onKeyUp={(e) => {
              if (e.key === "Enter") handleCheckPassword();
            }}
            trailingIcon={
              <Box alignItems={"center"}>
                <Tooltip
                  message={
                    isPwVisible
                      ? t("login.login.hidePW")
                      : t("login.login.showPW")
                  }
                >
                  <span
                    className={"cursor pointer"}
                    onClick={() => togglePWVisible(!isPwVisible)}
                  >
                    <Icons
                      variant="line"
                      label="preview"
                      isOn={!isPwVisible}
                      stroke={"#717985"}
                    />
                  </span>
                </Tooltip>
              </Box>
            }
          />
          <Button
            label={t("common.confirm")}
            btnStyle="fill"
            width={"360px"}
            classes="mg mt-40"
            state={password === "" ? "disable" : "default"}
            onClick={handleCheckPassword}
          />
        </Box>
      </Box>
    );
  };
  const renderProperPage = () => {
    if (loginState.actionInfo.authType === "PASSWORD") {
      if (currStep === "ING") {
        return renderCheckPassword();
      } else if (
        ["DOWNLOAD", "HTMLDOWNLOAD"].includes(loginState.actionInfo.urlAction)
      ) {
        return (
          <DownloadIng
            uid={loginState.actionInfo.uniqueId}
            otp={apiTokenAfterPw}
            actionInfo={loginState.actionInfo}
          />
        );
      } else if (currStep === "EXPIRED") {
        return (
          <SimpleLinkPage
            imagePath={require("../../../shared/images/common/restrictClient.png")}
            pageTitle={t("login.link.expired")}
            hasButtonToHome
            bodymsg={
              <span>
                <Typography exactColor={getColor("COLOR_TEXT_ERROR")}>
                  {t("login.link.expiredInfo1")}
                </Typography>
                {t("login.link.expiredInfo2")}
                {t("login.link.askAdmin")}
              </span>
            }
          />
        );
      } else {
        return (
          <NoAuthPreview
            uniqueId={loginState.actionInfo.uniqueId}
            urlAction={loginState.actionInfo.urlAction}
            syncId={loginState.actionInfo.syncId}
            webviewStatus={currStep}
            accessUrl={fileUrl}
            otp={apiTokenAfterPw}
          />
        );
      }
    } else if (loginState.actionInfo.authType === "LOGIN") {
      const user = getUserInfoFromLocalStorage() as string | "";
      const loginDto: LoginDto = JSON.parse(user);
      return (loginState.actionInfo.urlAction === "HTMLDOWNLOAD" ||
        loginDto?.ecoSystemLicenseStats?.allowWebView !== true) &&
        currStep === "SUCCESS" ? (
        <DownloadIng
          uid={loginState.actionInfo.uniqueId}
          actionInfo={loginState.actionInfo}
        />
      ) : (
        <></>
      );
    } else {
      if (
        ["DOWNLOAD", "HTMLDOWNLOAD"].includes(loginState.actionInfo.urlAction)
      ) {
        return (
          <DownloadIng
            uid={loginState.actionInfo.uniqueId}
            actionInfo={loginState.actionInfo}
          />
        );
      } else {
        return (
          <NoAuthPreview
            uniqueId={loginState.actionInfo.uniqueId}
            urlAction={loginState.actionInfo.urlAction}
            syncId={loginState.actionInfo.syncId}
            webviewStatus={currStep}
            accessUrl={fileUrl}
          />
        );
      }
    }
  };
  return renderProperPage();
};

export default WebusingLink;
